import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Check, ChevronLeft, ChevronRight, Copy, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { BackgroundTileCrop } from "@/components/Backgrounds/BackgroundTileCrop";
import { facetLabel } from "@/components/Backgrounds/facetLabels";
import { PreviewRatio } from "@/components/Backgrounds/library/LibraryToolbar";
import { PREVIEW_RATIO_ITEMS } from "@/components/Backgrounds/library/LibraryToolbar";
import { aspectClass } from "@/components/Backgrounds/ratios";
import { RefineBackgroundDialog } from "@/components/Backgrounds/RefineBackgroundDialog";
import { SocoAiLogo } from "@/components/Holo";
import { formatRelativeTime } from "@/components/reviews/utils";
import { Badge, Button, Dialog, Skeleton, Spinner, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { BackgroundAssetServices } from "@/services/bedrock/BackgroundAssetServices";
import {
  BACKGROUND_FACETS,
  BackgroundAsset,
  BackgroundAssetSize,
  BackgroundFacet,
  BackgroundVisibility,
} from "@/services/bedrock/dtos/BackgroundAsset";
import { designKeys } from "@/services/bedrock/queryKeys";

const GENERIC_ERROR = "Something went wrong. Try again.";
const FACETS = Object.keys(BACKGROUND_FACETS) as BackgroundFacet[];
// One height for the photo stage and the details beside it, so the dialog never jumps between photos.
const PANE_HEIGHT = "md:h-[min(72vh,640px)]";

/** The tags as words, e.g. ["Home exterior", "Coastal", "Golden hour"]; "none" is the absence of a tag. */
export function tagWords(asset: BackgroundAsset): string[] {
  return FACETS.flatMap(facet => {
    const value = asset[facet];
    return value && value !== "none" ? [facetLabel(facet, value)] : [];
  });
}

/** Where the photo came from: the panel's heading, so every photo's heading has the same short shape. */
const Origin: React.FC<{ asset: BackgroundAsset }> = ({ asset }) => {
  if (asset.coachId === null) return <>From the SocialCoach library</>;
  if (asset.source !== "uploaded") {
    return (
      <span className="inline-flex items-center gap-1.5">
        Created with
        <SocoAiLogo variant="color" height={14} className="translate-y-px" />
      </span>
    );
  }
  return <>{asset.createdBy?.name ? `Uploaded by ${asset.createdBy.name}` : "Uploaded by your team"}</>;
};

const SIZE_KIND_LABELS: Record<BackgroundAssetSize["kind"], string> = {
  made: "Made for this size",
  cropped: "Cropped",
  making: "Making…",
};
const RATIO_LABELS = Object.fromEntries(PREVIEW_RATIO_ITEMS.map(item => [item.value, item.label]));

/** The size as designs show it; an older response without sizes falls back to the master picture. */
function sizeFor(asset: BackgroundAsset, ratio: PreviewRatio): BackgroundAssetSize {
  return (
    asset.sizes?.find(size => size.ratio === ratio) ?? {
      ratio,
      url: asset.url,
      focal: asset.focal,
      compatible: asset.compatible,
      kind: "cropped",
    }
  );
}

/** One thumbnail per size, each the real crop at that size; picking one swaps the large photo. */
const SizeStrip: React.FC<{
  asset: BackgroundAsset;
  value: PreviewRatio;
  onChange(ratio: PreviewRatio): void;
}> = ({ asset, value, onChange }) => (
  <div className="flex items-end justify-center gap-3" role="radiogroup" aria-label="Sizes">
    {PREVIEW_RATIO_ITEMS.map(({ value: ratio, label }) => {
      const size = sizeFor(asset, ratio);
      const selected = ratio === value;
      const kind = SIZE_KIND_LABELS[size.kind];
      return (
        <Tooltip key={ratio}>
          <Tooltip.Trigger
            render={
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`${label}, ${kind}`}
                onClick={() => onChange(ratio)}
                className="group/size flex flex-col items-center gap-1 cursor-pointer"
              />
            }
          >
            <span
              className={cn(
                "relative block h-14 overflow-hidden rounded-md bg-white ring-offset-2 ring-offset-slate-100 transition-shadow",
                aspectClass(ratio),
                selected ? "ring-2 ring-primary" : "ring-1 ring-slate-300 group-hover/size:ring-slate-400"
              )}
            >
              <img
                src={size.url}
                alt=""
                className="size-full object-cover"
                style={{ objectPosition: size.focal }}
                draggable={false}
              />
              {size.kind === "making" && (
                <span className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <Spinner className="size-3.5 text-slate-600" />
                </span>
              )}
            </span>
            <span className={cn("text-[11px] font-semibold", selected ? "text-slate-900" : "text-slate-500")}>
              {label}
            </span>
          </Tooltip.Trigger>
          <Tooltip.Content>{kind}</Tooltip.Content>
        </Tooltip>
      );
    })}
  </div>
);

const SectionLabel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{children}</span>
);

// Arrow keys page through backgrounds unless the coach is typing or choosing in a control.
function isEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return true;
  return !!target.closest("[role='combobox'], [role='listbox'], [role='menu'], [role='radiogroup']");
}

// A dialog opened over this one (Create with AI, Refine) owns its own keys.
function isInOtherDialog(target: EventTarget | null, own: HTMLElement | null): boolean {
  if (!(target instanceof HTMLElement) || !own) return false;
  const dialog = target.closest("[role='dialog']");
  return !!dialog && dialog !== own.closest("[role='dialog']");
}

const CopyPromptButton: React.FC<{ prompt: string }> = ({ prompt }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <Button
      size="xs"
      variant="ghost"
      onClick={() => {
        void navigator.clipboard?.writeText(prompt);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="text-emerald-600" aria-hidden="true" /> : <Copy aria-hidden="true" />}
      Copy prompt
    </Button>
  );
};

export interface BackgroundDetailDialogProps {
  /** null = closed. */
  assetId: number | null;
  /** The tab's preview ratio, so the crop matches the grid. */
  previewRatio: PreviewRatio;
  canEdit: boolean;
  onClose(): void;
  /** Previous / next. */
  onOpenAsset(assetId: number): void;
  previousId?: number | null;
  nextId?: number | null;
  /** Opens Create with AI prefilled with this background's prompt. */
  onNewFromPrompt(prompt: string): void;
}

/**
 * What a coach sees when they open a background: the photo, whose it is, and what they can do
 * with it. Curation details (sizes, versions, usage, lineage, flags, tier) stay in the admin view.
 */
export const BackgroundDetailDialog: React.FC<BackgroundDetailDialogProps> = ({
  assetId,
  previewRatio,
  canEdit,
  onClose,
  onOpenAsset,
  previousId = null,
  nextId = null,
  onNewFromPrompt,
}) => {
  const queryClient = useQueryClient();
  const open = assetId != null;
  const { data: asset, isLoading } = useQuery({
    queryKey: designKeys.backgrounds.asset(assetId ?? 0),
    queryFn: () => BackgroundAssetServices.get(assetId!),
    enabled: open,
  });
  const [refineOpen, setRefineOpen] = React.useState(false);
  // Each photo opens at the grid's size, so it matches the tile that was clicked.
  const [shownRatio, setShownRatio] = React.useState<PreviewRatio>(previewRatio);
  React.useEffect(() => setShownRatio(previewRatio), [assetId, previewRatio]);

  const contentRef = React.useRef<HTMLDivElement>(null);
  const neighbors = React.useRef({ previousId, nextId, onOpenAsset });
  neighbors.current = { previousId, nextId, onOpenAsset };
  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isEditingTarget(event.target) ||
        isInOtherDialog(event.target, contentRef.current)
      )
        return;
      const { previousId: prev, nextId: next, onOpenAsset: go } = neighbors.current;
      if (event.key === "ArrowLeft" && prev != null) go(prev);
      if (event.key === "ArrowRight" && next != null) go(next);
    };
    // Capture: Base UI buttons stop arrow keys from bubbling, and focus often sits on ‹ › here.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  const setVisibility = useMutation({
    mutationFn: (visibility: Extract<BackgroundVisibility, "active" | "archived">) =>
      BackgroundAssetServices.update(asset!.id, { visibility }),
    onSuccess: saved => {
      queryClient.setQueryData(designKeys.backgrounds.asset(saved.id), saved);
      queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.all });
      if (saved.visibility === "archived") toast.success("Archived. Designs already using it keep it.");
    },
    onError: () => toast.error(GENERIC_ERROR),
  });

  // No scope: the child lands in the coach's own library, whichever library the source is in.
  const refine = async (instruction: string) => {
    try {
      await BackgroundAssetServices.refine(asset!.id, { instruction });
    } catch (error) {
      toast.error(GENERIC_ERROR);
      throw error;
    }
    queryClient.invalidateQueries({ queryKey: designKeys.backgrounds.requests({}).slice(0, 1) });
    toast.success("Refining… the new version will show up here when it's ready.");
    onClose();
  };

  const own = asset ? asset.coachId !== null : false;
  const prompt = own ? asset?.instruction ?? null : null;
  const tags = asset ? tagWords(asset) : [];

  const navButton = (direction: "previous" | "next") => {
    const id = direction === "previous" ? previousId : nextId;
    const Icon = direction === "previous" ? ChevronLeft : ChevronRight;
    return (
      <Button
        size="icon-lg"
        variant="outline"
        aria-label={direction === "previous" ? "Previous background" : "Next background"}
        disabled={id == null}
        onClick={() => id != null && onOpenAsset(id)}
        className={cn(
          "absolute top-1/2 z-10 -translate-y-1/2 rounded-full border-slate-200 bg-white/90 shadow-md backdrop-blur transition-opacity hover:bg-white disabled:opacity-0",
          // Quiet until the photo is hovered; always there for keyboard focus and touch.
          "md:opacity-0 md:group-hover/stage:opacity-100 md:focus-visible:opacity-100",
          direction === "previous" ? "left-3" : "right-3"
        )}
      >
        <Icon aria-hidden="true" />
      </Button>
    );
  };

  return (
    // minmax(0, 1fr): the popup is a grid, and a long title must wrap inside it rather than widen it.
    <Dialog
      open={open}
      onOpenChange={next => !next && onClose()}
      size="xl"
      className="grid-cols-[minmax(0,1fr)] overflow-hidden"
    >
      <div
        ref={contentRef}
        className="grid min-w-0 grid-cols-1 md:grid-cols-[minmax(0,1fr)_320px]"
        data-testid="background-detail"
      >
        {/* The photo on a quiet stage at the chosen size, with every size's crop in a strip under it. */}
        <div
          className={cn(
            "group/stage relative flex h-[60vh] flex-col items-center gap-4 bg-slate-100 px-8 pt-8 pb-4",
            PANE_HEIGHT
          )}
        >
          {navButton("previous")}
          <div className="flex min-h-0 w-full flex-1 items-center justify-center">
            {isLoading || !asset ? (
              <Skeleton className={cn("h-full max-w-full rounded-xl", aspectClass(shownRatio))} />
            ) : (
              <div
                className={cn("h-full max-h-full max-w-full rounded-xl shadow-xl", aspectClass(shownRatio))}
                data-testid="background-detail-preview"
              >
                <BackgroundTileCrop
                  asset={{
                    ...sizeFor(asset, shownRatio),
                    id: asset.id,
                    description: asset.description,
                    source: asset.source,
                  }}
                  ratio={shownRatio}
                  hideUploadGlyph
                  className="h-full"
                />
              </div>
            )}
          </div>
          {asset && (
            <div className="flex flex-col items-center gap-1.5">
              <SizeStrip asset={asset} value={shownRatio} onChange={setShownRatio} />
              <span className="text-xs text-slate-500" data-testid="background-detail-size">
                {RATIO_LABELS[shownRatio]} · {SIZE_KIND_LABELS[sizeFor(asset, shownRatio).kind]}
              </span>
            </div>
          )}
          {navButton("next")}
        </div>

        <aside className={cn("flex min-w-0 flex-col border-t border-slate-200 md:border-t-0 md:border-l", PANE_HEIGHT)}>
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5">
            <div className="flex items-start gap-3">
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {isLoading || !asset ? (
                  <Skeleton className="h-5 w-40" />
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <Dialog.Title className="text-base leading-snug">
                        <Origin asset={asset} />
                      </Dialog.Title>
                      {asset.visibility === "archived" && <Badge variant="label">Archived</Badge>}
                    </div>
                    {own && (
                      <span className="text-xs text-slate-500" data-testid="background-detail-when">
                        {formatRelativeTime(asset.createdAt)}
                      </span>
                    )}
                  </>
                )}
              </div>
              <Button size="icon-sm" variant="ghost" aria-label="Close" onClick={onClose} className="-mt-1 -mr-2">
                <X aria-hidden="true" />
              </Button>
            </div>

            {isLoading || !asset ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <>
                {/* One text per photo: the coach's own prompt when they wrote one, otherwise the description. */}
                {prompt ? (
                  <section className="flex flex-col gap-1.5" aria-label="Prompt">
                    <div className="flex items-center justify-between">
                      <SectionLabel>Prompt</SectionLabel>
                      <CopyPromptButton prompt={prompt} />
                    </div>
                    {/* Only the prompt scrolls when it's long; the rest of the panel stays put. */}
                    <p
                      className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700"
                      data-testid="background-detail-prompt"
                    >
                      {prompt}
                    </p>
                  </section>
                ) : (
                  <section className="flex flex-col gap-1.5" aria-label="Description">
                    <SectionLabel>Description</SectionLabel>
                    <p
                      className={cn(
                        "break-words text-sm leading-relaxed",
                        asset.description ? "text-slate-700" : "text-slate-400"
                      )}
                    >
                      {asset.description || "No description yet"}
                    </p>
                  </section>
                )}

                {tags.length > 0 && (
                  <section className="flex flex-col gap-1.5" aria-label="Tags">
                    <SectionLabel>Tags</SectionLabel>
                    <ul className="flex flex-wrap gap-1.5" data-testid="background-detail-tags">
                      {tags.map(tag => (
                        <li
                          key={tag}
                          className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                        >
                          {tag}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </div>

          {canEdit && asset && (
            <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-50 p-4">
              <Button onClick={() => setRefineOpen(true)} className="w-full">
                <Sparkles aria-hidden="true" />
                Refine with AI…
              </Button>
              {(prompt || own) && (
                <div className="flex gap-2">
                  {prompt && (
                    <Button variant="outline" onClick={() => onNewFromPrompt(prompt)} className="min-w-0 flex-1">
                      New from this prompt
                    </Button>
                  )}
                  {own &&
                    (asset.visibility === "archived" ? (
                      <Button
                        variant="outline"
                        onClick={() => setVisibility.mutate("active")}
                        loading={setVisibility.isPending}
                        className="flex-1"
                      >
                        <ArchiveRestore aria-hidden="true" />
                        Restore
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => setVisibility.mutate("archived")}
                        loading={setVisibility.isPending}
                        className={prompt ? "shrink-0" : "flex-1"}
                      >
                        <Archive aria-hidden="true" />
                        Archive
                      </Button>
                    ))}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {asset && (
        <RefineBackgroundDialog
          open={refineOpen}
          onOpenChange={setRefineOpen}
          asset={{ id: asset.id, url: sizeFor(asset, shownRatio).url, focal: sizeFor(asset, shownRatio).focal }}
          ratio={shownRatio}
          onSubmit={refine}
          busy={false}
        />
      )}
    </Dialog>
  );
};
